var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { formatCurrency } from "@rentiohq/shared-frontend/dist/utils/number.utils";
import { useState } from "react";
import { StyledError, StyledHelpText } from "../../../Form/Form.styled";
import { Icon } from "../../../Icon";
import { Labelled } from "../../../Labelled";
import { BaseInput } from "../../components/BaseInput";
import * as S from "./RentmatchScoreField.styled";
/** How far the slider reaches, unless a larger amount was typed. */
var SLIDER_RENT_MULTIPLE = 5;
var formatFactor = function (factor) { return factor.toFixed(1).replace(".", ","); };
/**
 * BaseInput hands its handler the value, not the change event, and an emptied
 * number field arrives as an empty string. Stored as null, which is what puts
 * the property back on the rule it was scored by before.
 */
var toNumberOrNull = function (value) {
    return value === "" || value === null || value === undefined ? null : Number(value);
};
/**
 * An arrow turning back on itself, the usual sign for putting something back as
 * it was. Drawn here rather than taken from the shared icon set, which has no
 * reset glyph: its nearest, `synchronizeArrows`, is two arrows in a circle and
 * reads as "fetch this again", which is a different promise.
 *
 * Thin lines on purpose. The link undoes work rather than doing any, and at
 * 1.5 against a 24 viewBox drawn at 13px it lands under a pixel on screen, so
 * it sits beside the text without weighing more than it does.
 */
var ResetArrow = function () { return (_jsxs("svg", { width: "13", height: "13", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "1.5", strokeLinecap: "round", strokeLinejoin: "round", children: [_jsx("polyline", { points: "1 4 1 10 7 10" }), _jsx("path", { d: "M3.51 15a9 9 0 1 0 2.13-9.36L1 10" })] })); };
export var RentmatchScoreField = function (props) {
    var _a;
    var uiSchema = props.uiSchema, idSchema = props.idSchema, formData = props.formData, _b = props.formContext, formContext = _b === void 0 ? {} : _b, _c = props.rawErrors, rawErrors = _c === void 0 ? [] : _c, onChange = props.onChange;
    var _d = uiSchema, getRent = _d.getRent, getDefaultIncome = _d.getDefaultIncome, getDefaultHousehold = _d.getDefaultHousehold, _e = _d.minFactor, minFactor = _e === void 0 ? 1.5 : _e;
    // Both halves report on the one field this block is bound to, so an error can
    // be about either of them and there can be two at once.
    //
    // A block that is closed hides what is wrong while the save keeps failing, so
    // an error holds it open rather than merely opening it once.
    var _f = useState(false), isExpanded = _f[0], setIsExpanded = _f[1];
    var isOpen = isExpanded || rawErrors.length > 0;
    var allFormData = ((_a = formContext.getAllFormData) === null || _a === void 0 ? void 0 : _a.call(formContext)) || {};
    var rent = (getRent === null || getRent === void 0 ? void 0 : getRent(allFormData)) || 0;
    var defaultIncome = (getDefaultIncome === null || getDefaultIncome === void 0 ? void 0 : getDefaultIncome(allFormData)) || 0;
    var defaultHousehold = getDefaultHousehold === null || getDefaultHousehold === void 0 ? void 0 : getDefaultHousehold(allFormData);
    var value = formData || {};
    var income = typeof value.perfectIncome === "number" ? value.perfectIncome : undefined;
    var household = typeof value.maxHousehold === "number" ? value.maxHousehold : undefined;
    var set = function (next) {
        return onChange(__assign(__assign({}, value), next));
    };
    /**
     * An empty number field steps from zero, so the first click on the arrows
     * threw away the amount the broker was being shown and started counting at 1.
     *
     * Entering the field fills in that amount, so the arrows step from it, and
     * leaving again without having changed it empties the field back out. Only a
     * number the broker actually chose is stored, and clicking in and out is not
     * a choice.
     */
    var _g = useState({}), seeded = _g[0], setSeeded = _g[1];
    var seedOnFocus = function (key, current, fallback) {
        var _a, _b;
        // Nothing to seed with when the rent is still blank, and a household of
        // zero is not a number anybody may save.
        if (current !== undefined || !fallback) {
            return;
        }
        setSeeded(__assign(__assign({}, seeded), (_a = {}, _a[key] = fallback, _a)));
        set((_b = {}, _b[key] = fallback, _b));
    };
    var clearOnBlur = function (key, current) {
        var _a, _b;
        if (seeded[key] === undefined) {
            return;
        }
        if (current === seeded[key]) {
            set((_a = {}, _a[key] = null, _a));
        }
        setSeeded(__assign(__assign({}, seeded), (_b = {}, _b[key] = undefined, _b)));
    };
    // Rounded up, so the lowest position on the slider is a value the broker is
    // allowed to save rather than one that fails validation.
    var minimum = Math.ceil(rent * minFactor);
    var maximum = Math.max(rent * SLIDER_RENT_MULTIPLE, income !== null && income !== void 0 ? income : 0, minimum);
    var shownIncome = income !== null && income !== void 0 ? income : defaultIncome;
    var shownHousehold = household !== null && household !== void 0 ? household : defaultHousehold;
    // The track is painted rather than left to the browser, so how far it is
    // filled has to be worked out here. Clamped, because the amount typed in the
    // field beside it may sit outside the slider's own range.
    var fillPercent = maximum > minimum
        ? Math.min(100, Math.max(0, ((shownIncome - minimum) / (maximum - minimum)) * 100))
        : 0;
    // The closed block says what the property asks today in one line. A bedroom
    // count the form does not know yet leaves the household out of it rather than
    // printing a gap, and a household of one gets its own wording rather than a
    // plural that reads wrong in every language here.
    var getSummarySentence = function () {
        // An amount, not a multiple of the rent: the closed block has to be read at
        // a glance, and a factor asks the broker to do the sum themselves. The
        // multiple is still spelled out under the slider, where it explains where
        // the amount comes from.
        // No cents: these are thresholds a broker picks, not amounts anybody is
        // charged, and a trailing ",00" on every one of them is noise.
        var values = { income: formatCurrency(shownIncome, false) };
        if (shownHousehold === undefined) {
            return getLocalizedText("property.rentmatch.score_settings.summary.income_only", values);
        }
        if (shownHousehold === 1) {
            return getLocalizedText("property.rentmatch.score_settings.summary.one_person", values);
        }
        return getLocalizedText("property.rentmatch.score_settings.summary", __assign(__assign({}, values), { people: "".concat(shownHousehold) }));
    };
    return (_jsxs(S.Block, { children: [_jsxs(S.Summary, { type: "button", onClick: function () { return setIsExpanded(!isOpen); }, children: [_jsxs(S.SummaryText, { children: [_jsx(S.Title, { children: getLocalizedText("property.rentmatch.score_settings.title") }), rent > 0 && _jsx(S.Sentence, { children: getSummarySentence() })] }), _jsx(S.Chevron, { "$isOpen": isOpen, children: _jsx(Icon, { source: "chevronDown" }) })] }), isOpen && (_jsxs(S.Body, { children: [_jsx(Labelled, { label: getLocalizedText("property.rentmatch.perfect_income.label"), optional: true, children: _jsxs(S.Row, { children: [_jsxs(S.SliderColumn, { children: [_jsx(S.Slider, { type: "range", min: minimum, max: maximum, step: 10, "$isSet": income !== undefined, "$fillPercent": fillPercent, value: shownIncome, disabled: rent <= 0, onChange: function (event) {
                                                return set({ perfectIncome: Number(event.target.value) });
                                            } }), _jsxs(S.Bounds, { children: [_jsx("span", { children: "".concat(formatFactor(minFactor), "x") }), rent > 0 && (_jsx(StyledHelpText, { children: getLocalizedText("property.rentmatch.perfect_income.help", {
                                                        // What the amount means as a rule, which is how the
                                                        // office table states the same thing.
                                                        factor: formatFactor(shownIncome / rent),
                                                        rent: formatCurrency(rent, false),
                                                    }) })), _jsx("span", { children: "".concat(SLIDER_RENT_MULTIPLE, "x") })] })] }), _jsx(S.AmountInput, { children: _jsx(BaseInput, { id: "".concat(idSchema.$id, "-income"), type: "number", value: income !== null && income !== void 0 ? income : "", placeholder: defaultIncome ? "".concat(Math.round(defaultIncome)) : "", suffix: "EUR", onFocus: function () {
                                            return seedOnFocus("perfectIncome", income, defaultIncome);
                                        }, onBlur: function () { return clearOnBlur("perfectIncome", income); }, onChange: function (next) {
                                            return set({ perfectIncome: toNumberOrNull(next) });
                                        } }) })] }) }), _jsxs(S.HouseholdRow, { children: [_jsx(S.HouseholdLabel, { children: _jsx(Labelled, { label: getLocalizedText("property.rentmatch.max_household.label"), optional: true }) }), _jsx(S.HouseholdInput, { children: _jsx(BaseInput, { id: "".concat(idSchema.$id, "-household"), type: "number", 
                                    // The same icon that counts adults on a candidate's row, so the
                                    // number the broker sets here and the number they read there
                                    // are visibly the same thing.
                                    suffix: _jsx(Icon, { source: "contact" }), value: household !== null && household !== void 0 ? household : "", placeholder: defaultHousehold === undefined ? "" : "".concat(defaultHousehold), onFocus: function () {
                                        return seedOnFocus("maxHousehold", household, defaultHousehold);
                                    }, onBlur: function () { return clearOnBlur("maxHousehold", household); }, onChange: function (next) {
                                        return set({ maxHousehold: toNumberOrNull(next) });
                                    } }) })] }), (income !== undefined || household !== undefined) && (_jsx(S.ResetRow, { children: _jsxs(S.Reset, { type: "button", onClick: function () {
                                setSeeded({});
                                onChange({ perfectIncome: null, maxHousehold: null });
                            }, children: [_jsx(ResetArrow, {}), getLocalizedText("property.rentmatch.score_settings.reset")] }) })), rawErrors.map(function (error) { return (_jsx(StyledError, { children: error }, error)); })] }))] }));
};
